import React, { useState, useEffect } from 'react';
import DataTable from './components/DataTable/DataTable';
import SessionForm from './components/SessionForm/SessionForm';
import ParticipantsModal from './components/ParticipantsModal/ParticipantsModal';
import { fetch_video_call_sessions, video_room_participants, delete_video_session } from '../../Services/Service';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './ProSession.module.css';
import ParticipentModel from './ParticipentModel';
import StatsComponent from './components/StatsComponent';
import RecordingModal from './RecordingModal';

const ProSession = () => {
  const [sessions, setSessions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedSessionParticipants, setSelectedSessionParticipants] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [statsData, setStatsData] = useState({});

  const [openRecordingSession,setOpenRecordingSession] = useState(null);

  // Transform API response to match existing data structure
  const transformSessionData = (apiSession) => {
    return {
      id: apiSession.id,
      sessionName: apiSession.sessionName,
      sessionLink: apiSession.userUrl,
      sessionDetails: apiSession.sessionDetails,
      scheduledDate: new Date(apiSession.scheduledDate).toLocaleDateString('en-GB') + ' ' + 
                     new Date(apiSession.scheduledDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      rawScheduledDate: new Date(apiSession.scheduledDate),
      host: apiSession.host,
      participants: apiSession.enrolled,
      limit: apiSession.limit,
      join: apiSession.join||0,
      bookedParticipent: apiSession.bookedParticipent||0,
      sessionType: apiSession.sessionType.toLowerCase() === 'onetoone' ? 'private' : 'group',
      program: apiSession.program || [],
      division: apiSession.division || [],
      clinics: apiSession.clinic || [],
      practice: 'General', // Default value as API doesn't provide this
      maxParticipants: apiSession.limit,
      hostMobile: apiSession.host,
      participantMobiles: [],
      participantsList: apiSession.participantsList || [],
      status: apiSession.status,
      uid: apiSession.uid,
      isStart:apiSession.isStart,
      
    };
  };

  // Load sessions from API
  const loadSessions = async () => {
  try {
    setLoading(true);
    
    // Fix: Pass pagination parameters properly
    const response = await fetch_video_call_sessions(rowsPerPage, currentPage);
    
    if (response.status === 200 && response.data.code === 200) {
      const apiSessions = response.data.data.videoCalls || [];
      setStatsData(response.data.data.totalResult || {});
      const transformedSessions = apiSessions.map(transformSessionData);
      setSessions(transformedSessions);
      
      // Set total items for pagination
      setTotalItems(response.data.data.totalResult.total || 0);

      console.log("session response",response);
      
      // Debug logging to check pagination
      console.log('Pagination Debug:', {
        currentPage,
        rowsPerPage,
        totalItems: response.data.data.total,
        receivedItems: apiSessions.length,
        totalPages: Math.ceil((response.data.data.total || 0) / rowsPerPage)
      });
      
    } else {
      console.error('Failed to fetch sessions:', response);
      setSessions([]);
      setTotalItems(0);
    }
  } catch (error) {
    console.error('Error loading sessions:', error);
    setSessions([]);
    setTotalItems(0);
    toast.error('Failed to load sessions. Please try again.');
  } finally {
    setLoading(false);
  }
};

  // Load sessions on component mount and when pagination changes
  useEffect(() => {
    loadSessions();
  }, [currentPage, rowsPerPage]);

  const columns = [
    { key: 'sessionName', label: 'Session Name', sortable: true, bodyClassName: 'sessionNameBody' },
    { key: 'sessionDetails', label: 'Description', sortable: false },
    { key: 'scheduledDate', label: 'Date/Time', sortable: true },
    { key: 'sessionType', label: 'Session Type', sortable: true },
    { key: 'host', label: 'Host', sortable: true },
    { key: 'participants', label: 'Invited', sortable: true, headerClassName: 'participantsHeader',isRow:true },
    { key: 'bookedParticipent', label: 'Booked', sortable: true },
    { key: 'join', label: 'Attended', sortable: true },
    { key: 'limit', label: 'Capacity', sortable: true },
    // { key: 'limit', label: 'Status', sortable: true },
    { key: 'actions', label: 'Action', sortable: false }
  ];

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const handleEdit = (session) => {
    setEditingSession(session);
    setShowForm(true);
  };

  const handleDelete = async (sessionId) => {
  if (window.confirm('Are you sure you want to delete this session?')) {
    try {
      setDeletingSessionId(sessionId);
      
      const response = await delete_video_session(sessionId);
      
      if (response.status === 200 || response.status === 204) {
        toast.success('Session deleted successfully!');
        
        // After deletion, if current page becomes empty and it's not the first page, go to previous page
        const remainingSessions = sessions.length - 1;
        if (remainingSessions === 0 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          await loadSessions();
        }
      } else {
        console.error('Failed to delete session:', response);
        toast.error('Failed to delete session. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session. Please try again.');
    } finally {
      setDeletingSessionId(null);
    }
  }
};

  const handleSave = async (sessionData) => {
    try {
      if (editingSession) {
        // Update existing session - the API call is already handled in SessionForm
        // Reload sessions to get updated data
        await loadSessions();
      } else {
        // Add new session - the API call is already handled in SessionForm
        // Reload sessions to get updated data
        await loadSessions();
      }
      setShowForm(false);
      setEditingSession(null);
    } catch (error) {
      console.error('Error saving session:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSession(null);
  };

  const handleViewParticipants = async (session) => {
  try {
    setParticipantsLoading(true);
    setSelectedSessionId(session.id);
    setShowParticipantsModal(true);
    
    // First show existing participants from session data
    setSelectedSessionParticipants(session.participantsList || []);
    
    // Then fetch updated participants from API
    const response = await video_room_participants(session.id);
    
    if (response.data.code === 200) { // Changed this line
      const apiParticipants = response.data.data.videoCallParticipants || [];
      console.log(apiParticipants)
      setSelectedSessionParticipants(apiParticipants.map((item)=>{
        return {
          ...item,
          mobile:item.mobileNo
        }
      }));
    }
  } catch (error) {
    console.error('Error fetching participants:', error);
    // Keep existing participants if API fails
  } finally {
    setParticipantsLoading(false);
  }
};



  const handleCloseParticipantsModal = () => {
    setShowParticipantsModal(false);
    setSelectedSessionParticipants([]);
    setSelectedSessionId(null);
    setParticipantsLoading(false);
  };

  const handleUpdateParticipants = async (updatedList) => {
  // Update the participants in the modal
  setSelectedSessionParticipants(updatedList);
  
  // Refresh the main sessions list to get updated participant counts
  await loadSessions();
};

  const renderActions = (session) => (
  <div className={styles.actionButtons}>
    <button 
      className={styles.participantsBtn}
      onClick={() => handleViewParticipants(session)}
      title="View participants"
      disabled={deletingSessionId === session.id}
    >
      <i className="bi bi-people"></i>
    </button>
    <span className={styles.actionSeparator}>|</span>
    <button 
      className={styles.editBtn}
      onClick={() => handleEdit(session)}
      title={`Edit session`}
      disabled={deletingSessionId === session.id }
    >
      <i className="bi bi-pencil-square"></i>
    </button>
    <button 
      className={styles.editBtn}
      onClick={() => setOpenRecordingSession(session)}
      title={`Video recordings`}
      disabled={deletingSessionId === session.id }
    >
      <i className="bi bi-eye"></i>
    </button>
    <span className={styles.actionSeparator}>|</span>
    <button 
      className={styles.deleteBtn}
      onClick={() => handleDelete(session.id)}
      title="Delete session"
      disabled={deletingSessionId === session.id || session.isStart}
    >
      {deletingSessionId === session.id ? (
        <div className={styles.smallSpinner}></div>
      ) : (
        <i className="bi bi-trash3"></i>
      )}
    </button>
    
  </div>
);

  

  
const [inviteOpen,setInviteOpen] =useState(false);
const [inviteRow,setInviteRow] =useState({});
const handleRow = ({key,value,...rest})=>{
  switch(key){
    case 'participants':    
    return   <>
      <p key={`row__${rest.id}`} style={{
        color:'blue',
        textDecoration:'underline',
        cursor:'pointer'
      }} onClick={()=>{
        console.log(rest);
        setInviteRow(rest);
        setInviteOpen(true);
      }}>{value}</p></>
    default:
      return value;
  }
}
  if (showForm) {
    return (
      <SessionForm
        session={editingSession}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

 if (showParticipantsModal) {
  return (
    <ParticipantsModal
      participants={selectedSessionParticipants}
      onClose={handleCloseParticipantsModal}
      onUpdate={handleUpdateParticipants}
      loading={participantsLoading}
      roomId={selectedSessionId}
    />
  );
}

  return (
   
    <div className="projects-section">


      {inviteOpen &&
       <ParticipentModel 
        data={inviteRow} 
        open={inviteOpen} 
        onClose={()=>setInviteOpen(false)}
       />
      }
      {openRecordingSession &&
       <RecordingModal 
        session={openRecordingSession} 
        onClose={()=>setOpenRecordingSession(null)}
       />
      }
      <div className={styles.sessionHeader}>
          <h1>Video Sessions</h1>
          <button 
            className={styles.createNewBtn}
            onClick={() => setShowForm(true)}
          >
            Create New
          </button>
        </div>
      
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading sessions...</p>
        </div>
      ) : (
        <>
        <StatsComponent data={statsData}/>
        <DataTable
        handleRow={handleRow}
          data={sessions}
          columns={columns}
          renderActions={renderActions}
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setCurrentPage(1);
          }}
        />
        </>
      )}

       <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
      style={{ zIndex: 9999999999 }}
    />
    </div>
  );
};

export default ProSession;